import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Clock, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function OfferPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const suggestedAction = params.get("action") || "";

  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [decision, setDecision] = useState(null);   // "accept" | "decline"
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState("");
  const [done, setDone] = useState(null);   // final state after submit

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/buyback/offer/${id}`, { params: { token } })
      .then((r) => {
        if (!alive) return;
        setOffer(r.data);
      })
      .catch((e) => {
        if (alive) setError(e?.response?.data?.detail || "Angebot nicht gefunden oder ungültig.");
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id, token]);

  const submit = async (action) => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/buyback/offer/${id}/decision`, {
        token, action, reason: action === "decline" ? reason : "",
      });
      setDone({ ok: true, action, ...data });
      toast.success(action === "accept" ? "Angebot angenommen — Danke!" : "Angebot abgelehnt");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Konnte nicht verarbeitet werden";
      setDone({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center text-white">
      <Loader2 className="w-6 h-6 animate-spin" />
    </div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-6" data-testid="offer-error">
        <div className="max-w-md text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto" />
          <h1 className="font-display text-2xl font-bold text-white mt-4">Angebot ungültig</h1>
          <p className="text-sm text-zinc-400 mt-2">{error}</p>
          <p className="text-xs text-zinc-600 mt-6">Bei Fragen: +43 676 4440304 · store@hausdeshandys.at</p>
        </div>
      </div>
    );
  }

  const finalPrice = Number(offer?.final_offer || 0);
  const already = offer?.status === "customer_accepted" || offer?.status === "customer_declined";
  const validUntil = (offer?.offer_valid_until || "").replace("T", " ").slice(0, 16);
  const ai = offer?.ai_estimate || {};

  return (
    <div className="min-h-screen bg-[#0A0A0B] py-16 px-6" data-testid="offer-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#DC1928]/12 border border-[#DC1928]/30">
            <ShieldCheck className="w-3.5 h-3.5 text-[#FF4F5E]" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#FF4F5E] font-bold">Verbindliches Kaufangebot</span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white mt-4 leading-tight">
            Hallo {offer?.customer_name},<br/>
            <span className="text-zinc-500">unser Angebot für dein Gerät.</span>
          </h1>
        </div>

        {done?.ok ? (
          <div className={`p-8 rounded-3xl border text-center ${
            done.action === "accept"
              ? "bg-emerald-500/10 border-emerald-500/30"
              : "bg-zinc-500/10 border-zinc-500/25"
          }`} data-testid="offer-done">
            {done.action === "accept" ? (
              <>
                <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto" />
                <h2 className="font-display text-2xl font-bold text-white mt-4">Vielen Dank!</h2>
                <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
                  Du hast unser Angebot über <strong className="text-white">€ {finalPrice.toFixed(0)}</strong> angenommen. Wir melden uns in Kürze mit den nächsten Schritten (Termin & benötigte Unterlagen).
                </p>
                <p className="text-xs text-zinc-500 mt-6">Bei Fragen: +43 676 4440304 · store@hausdeshandys.at</p>
              </>
            ) : (
              <>
                <XCircle className="w-16 h-16 text-zinc-400 mx-auto" />
                <h2 className="font-display text-2xl font-bold text-white mt-4">Schade!</h2>
                <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
                  Du hast unser Angebot abgelehnt. Falls du es dir anders überlegst, melde dich gerne — vielleicht können wir noch nachverhandeln.
                </p>
                <p className="text-xs text-zinc-500 mt-6">Kontakt: +43 676 4440304</p>
              </>
            )}
          </div>
        ) : already ? (
          <div className="p-8 rounded-3xl bg-white/5 border border-white/10 text-center" data-testid="offer-already">
            <Clock className="w-12 h-12 text-zinc-500 mx-auto" />
            <h2 className="font-display text-xl font-bold text-white mt-3">Bereits beantwortet</h2>
            <p className="text-sm text-zinc-400 mt-2">
              Dieses Angebot wurde bereits {offer.status === "customer_accepted" ? "angenommen" : "abgelehnt"}. Bei Fragen melde dich bitte telefonisch.
            </p>
          </div>
        ) : (
          <>
            {/* Big price card */}
            <div className="p-6 md:p-8 rounded-3xl bg-gradient-to-br from-[#15151A] to-[#0A0A0B] border border-white/8" data-testid="offer-card">
              <div className="text-[11px] uppercase tracking-[0.28em] text-zinc-500 font-bold">Für dein Gerät</div>
              <div className="font-display text-lg font-semibold text-white mt-1">
                {offer?.brand} {offer?.model} {offer?.storage && <span className="text-zinc-500">· {offer?.storage}</span>}
              </div>
              <div className="mt-6 pt-6 border-t border-white/8 flex items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF4F5E] font-bold">Verbindliches Angebot</div>
                  <div className="font-display text-6xl font-bold text-white numeric tracking-tight leading-none mt-2">€ {finalPrice.toFixed(0)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Gültig bis</div>
                  <div className="text-sm text-white font-semibold mt-1">{validUntil}</div>
                </div>
              </div>
              {ai?.min_price && (
                <div className="mt-5 p-3 rounded-xl bg-white/5 text-xs text-zinc-400 flex items-start gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-zinc-500 mt-0.5 shrink-0" />
                  <span>Die ursprüngliche <span className="text-zinc-300">unverbindliche</span> KI-Schätzung lag bei € {ai.min_price} – € {ai.max_price}. Der aktuelle Preis wurde manuell durch unser Team festgelegt.</span>
                </div>
              )}
              <p className="mt-5 text-xs text-zinc-500 leading-relaxed">
                Der Preis gilt vorbehaltlich der korrekten Angaben und einer Geräteprüfung vor Ort. Bitte bring einen Lichtbildausweis und den Eigentumsnachweis mit.
              </p>
            </div>

            {/* Decision */}
            {decision === "decline" ? (
              <div className="mt-6 p-6 rounded-3xl bg-white/5 border border-white/10">
                <Label>Grund für die Ablehnung (optional)</Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="z. B. Preis zu niedrig, anderswo besser, überlege noch…"
                  className="bg-[#0A0A0B] border-white/10 text-white mt-2"
                  data-testid="offer-decline-reason"
                />
                <div className="mt-4 flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDecision(null)}
                    className="bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white rounded-full"
                  >
                    Zurück
                  </Button>
                  <Button
                    onClick={() => submit("decline")}
                    disabled={submitting}
                    className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white rounded-full"
                    data-testid="offer-confirm-decline"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ablehnung senden"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  onClick={() => submit("accept")}
                  disabled={submitting}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-full h-14 text-base font-semibold"
                  data-testid="offer-accept-btn"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-5 h-5 mr-2" /> Angebot annehmen</>}
                </Button>
                <Button
                  onClick={() => setDecision("decline")}
                  disabled={submitting}
                  variant="outline"
                  className="bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white rounded-full h-14 text-base font-semibold"
                  data-testid="offer-decline-btn"
                >
                  <XCircle className="w-5 h-5 mr-2" /> Ablehnen
                </Button>
              </div>
            )}

            <div className="mt-8 text-center text-xs text-zinc-600">
              Bei Fragen: <a href="tel:+436764440304" className="text-[#FF4F5E]">+43 676 4440304</a> · <a href="mailto:store@hausdeshandys.at" className="text-[#FF4F5E]">store@hausdeshandys.at</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">{children}</div>;
}
